import { format } from "date-fns";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryString } from "../../hooks/use_query_string";
import { localesByLanguage } from "../../offering/locale_data";
import { formatCurrency as libFormatCurrency } from "../currency/currency";
import { he, enUS, pl } from "date-fns/locale";
import {
  getLanguageComponent,
  getSystemLocale,
  normalizeLanguage,
} from "../locale/locale";
import { isEmpty } from "../js_utils/lang_utils";
import { merge } from "lodash";
import { enLocale } from "@/offering/locale_data";

export interface Locale {
  languageTag: string;
  baseLanguageTag: string;
  direction: "ltr" | "rtl";
}

type FBTTranslateFunc = (
  text: string,
  options?: { description?: string }
) => string;
type FBTPluralFunc = (
  singular: string,
  count: number,
  options: { many: string; name: string; showCount?: "yes" | "no" | "ifMany" }
) => string;
type FBTParamFunc = (name: string, value: string | number) => string;
type FBTFormatDateByPatternFunc = (date: Date, pattern: string) => string;
type FBTFormatDateFunc = (
  date: Date,
  options?: Intl.DateTimeFormatOptions
) => string;
type FBTFormatTimeFunc = (date: Date) => string;
type FBTFormatCurrencyFunc = (
  amount: number,
  currency: string | null
) => string;
type FBTTranslateOptions = { description?: string };

export interface FBT {
  (text: string, options?: FBTTranslateOptions): string;
  plural: FBTPluralFunc;
  param: FBTParamFunc;
  formatDateByPattern: FBTFormatDateByPatternFunc;
  formatDate: FBTFormatDateFunc;
  formatTime: FBTFormatTimeFunc;
  formatCurrency: FBTFormatCurrencyFunc;
}

interface I18nContext {
  locale: Locale;
  onChangeLocale: (locale: Locale) => void;
  onAddToDictionary: (dictionary: Dictionary) => void;
  fbt: FBT;
}

const initialFBT: FBT = Object.assign(() => {
  return "";
});

const I18nContext = createContext<I18nContext>({
  locale: {
    languageTag: getSystemLocale(),
    baseLanguageTag: getLanguageComponent(getSystemLocale()),
    direction: "ltr",
  },
  onChangeLocale: () => {},
  onAddToDictionary: () => {},
  fbt: initialFBT,
});

interface I18nProviderProps {
  fallbackLocale: Locale;
  initialDictionary?: Dictionary;
  children?: React.ReactNode;
}

export interface Dictionary {
  [languageTag: string]: {
    [text: string]: string;
  };
}

export const LOCALE_KEY = "flexspace:locale";

function isLanguageWhitelisted(locale: string) {
  const whitelist = Object.keys(localesByLanguage);

  return whitelist.some((l) => locale === l);
}

function getDefaultLocale(
  qs: { [queryParams: string]: string | undefined },
  fallbackLocale: Locale
) {
  let qsLang = qs.lang;

  if (qsLang) {
    qsLang = normalizeLanguage(qsLang);

    if (!isLanguageWhitelisted(qsLang)) {
      localStorage.setItem(LOCALE_KEY, "en");
      return fallbackLocale;
    }

    localStorage.setItem(LOCALE_KEY, qsLang);
    return localesByLanguage[qsLang];
  }

  let storageLang = localStorage.getItem(LOCALE_KEY);

  if (storageLang) {
    storageLang = normalizeLanguage(storageLang);
    if (!isLanguageWhitelisted(storageLang)) {
      localStorage.setItem(LOCALE_KEY, "en");
      return fallbackLocale;
    }

    return localesByLanguage[storageLang];
  }

  let systemLang = getSystemLocale();
  systemLang = normalizeLanguage(systemLang);

  if (!isLanguageWhitelisted(systemLang)) {
    localStorage.setItem(LOCALE_KEY, "en");
    return fallbackLocale;
  }

  const locale = localesByLanguage[systemLang];

  if (!locale) {
    localStorage.setItem(LOCALE_KEY, "en");
    return fallbackLocale;
  }

  localStorage.setItem(LOCALE_KEY, locale.languageTag);
  return locale;
}

const tokenRegex = RegExp(/{(.+?)}/g);

const TRANSLATION_WHITELIST = [
  "wework",
  "mdot",
  "rooms",
  "brain-embassy",
  "coventus",
];

export function I18nProvider(props: I18nProviderProps) {
  const { children, fallbackLocale, initialDictionary = {} } = props;
  const qs = useQueryString();
  const [locale, setLocale] = useState<Locale>(
    TRANSLATION_WHITELIST.some((pn) => window.location.pathname.includes(pn))
      ? getDefaultLocale(qs, fallbackLocale)
      : fallbackLocale
  );
  const [dictionary, setDictionary] = useState<Dictionary>(initialDictionary);

  const translate: FBTTranslateFunc = useCallback(
    (text, options) => {
      return fbtTranslate(dictionary, locale, fallbackLocale)(text, options);
    },
    [dictionary, locale, fallbackLocale]
  );

  const param: FBTParamFunc = useCallback((name, value) => {
    return fbtParam(name, value);
  }, []);

  const plural: FBTPluralFunc = useCallback(
    (singular, count, options) => {
      return fbtPlural(param)(singular, count, options);
    },
    [param]
  );

  const formatDateByPattern: FBTFormatDateByPatternFunc = useCallback(
    (date, formatPattern) => {
      const lang = getLanguageComponent(locale.languageTag);
      if (lang === "he") {
        return format(date, formatPattern, { locale: he });
      } else if (lang === "pl") {
        return format(date, formatPattern, { locale: pl });
      }

      return format(date, formatPattern, { locale: enUS });
    },
    [locale.languageTag]
  );

  const formatTime: FBTFormatTimeFunc = useCallback(
    (date) => {
      return date.toLocaleTimeString(locale.languageTag, {
        hour: "numeric",
        minute: "numeric",
      });
    },
    [locale.languageTag]
  );

  const formatDate: FBTFormatDateFunc = useCallback(
    (date, options) => {
      return date.toLocaleDateString(
        locale.languageTag,
        options || {
          month: "short",
          day: "numeric",
        }
      );
    },
    [locale.languageTag]
  );

  const formatCurrency: FBTFormatCurrencyFunc = useCallback(
    (amount, currency) => {
      return libFormatCurrency(amount, currency, locale.languageTag);
    },
    [locale.languageTag]
  );

  const handleAddToDictionary = useCallback((nextDictionary) => {
    setDictionary((dictionary) => merge(dictionary, nextDictionary));
  }, []);

  const handleChangeLocale = useCallback((locale: Locale) => {
    setLocale(locale);
    localStorage.setItem(LOCALE_KEY, locale.languageTag);
  }, []);

  useEffect(() => {
    document.documentElement.dir = locale.direction;
    document.documentElement.lang = locale.baseLanguageTag;
  }, [locale]);

  const fbt: FBT = useMemo(
    () =>
      Object.assign(
        translate,
        { param },
        { plural },
        { formatDateByPattern },
        { formatDate },
        { formatCurrency },
        { formatTime }
      ),
    [
      translate,
      param,
      plural,
      formatDateByPattern,
      formatDate,
      formatCurrency,
      formatTime,
    ]
  );

  return (
    <I18nContext.Provider
      value={{
        locale,
        onChangeLocale: handleChangeLocale,
        onAddToDictionary: handleAddToDictionary,
        fbt,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

/**
 * This function translates strings.
 *
 * In case of interpolation, e.g. fbt('Hello, ' + fbt.param('name', value))
 * The resulting string is "Hello, {name:value}"
 *
 * Translation tries to replace the token `name` with `value` such that
 * given `value=Peter` it becomes "Hello, Peter"
 */
export function fbtTranslate(
  dictionary: Dictionary,
  locale: Locale,
  fallbackLocale?: Locale
): FBTTranslateFunc {
  return function translate(text, _options) {
    const [marshalledText, tokens] = getTokenizedTextAndTokens(text);

    // Check if there is a translation for tokenized strings with specific values
    // e.g. tokenized string "{number of likes} likes"
    // user translated "2 likes" to "II likes" instead of leaving default
    if (!isEmpty(tokens)) {
      let str = marshalledText;

      Object.keys(tokens).forEach((token) => {
        const value = tokens[token];

        str = str.replace(`{${token}}`, value);
      });

      let trs = getTranslation(str, dictionary, locale, fallbackLocale);

      if (trs) {
        return trs;
      }
    }

    let translation = getTranslation(
      marshalledText,
      dictionary,
      locale,
      fallbackLocale,
      true
    );

    if (translation === false) {
      return marshalledText;
    }

    if (!isEmpty(tokens)) {
      Object.keys(tokens).forEach((token) => {
        const value = tokens[token];

        if (typeof translation === "string") {
          translation = translation.replace(`{${token}}`, value);
        }
      });
    }

    return translation;
  };
}

function getTokenizedTextAndTokens(
  text: string
): [string, { [key: string]: string }] {
  let marshalledText = text;

  const tokens: { [key: string]: string } = {};

  let match;
  while ((match = tokenRegex.exec(text)) !== null) {
    const separatorIndex = match[1].indexOf(":");
    const token = match[1].substr(0, separatorIndex);
    const value = match[1].substr(separatorIndex + 1);

    marshalledText = marshalledText.replace(match[0], `{${token}}`);
    tokens[token] = value;
  }

  return [marshalledText, tokens];
}

function getTranslation(
  text: string,
  dictionary: Dictionary,
  locale: Locale,
  fallbackLocale?: Locale,
  checkIfNotFound?: boolean
): string | false {
  if (text === "") {
    // no translation for empty string
    return false;
  }

  if (dictionary[locale.languageTag] && dictionary[locale.languageTag][text]) {
    return dictionary[locale.languageTag][text];
  } else if (
    dictionary[getLanguageComponent(locale.languageTag)] &&
    dictionary[getLanguageComponent(locale.languageTag)][text]
  ) {
    return dictionary[getLanguageComponent(locale.languageTag)][text];
  } else if (
    fallbackLocale &&
    dictionary[fallbackLocale.languageTag] &&
    dictionary[fallbackLocale.languageTag][text]
  ) {
    return dictionary[fallbackLocale.languageTag][text];
  } else if (
    fallbackLocale &&
    dictionary[getLanguageComponent(fallbackLocale.languageTag)] &&
    dictionary[getLanguageComponent(fallbackLocale.languageTag)][text]
  ) {
    return dictionary[getLanguageComponent(fallbackLocale.languageTag)][text];
  }

  if (checkIfNotFound && locale.languageTag !== enLocale.languageTag) {
    console.log("Translation not found for", text);
  }
  return false;
}

export const fbtParam: FBTParamFunc = function fbtParam(
  name: string,
  value: string | number
) {
  return `{${name}:${value}}`;
};

export function fbtPlural(param: FBTParamFunc): FBTPluralFunc {
  return function plural(singular, count, options) {
    if (count > 1) {
      if (options.showCount === "ifMany" || options.showCount === "yes") {
        return `${param(options.name, count)} ${options.many}`;
      }

      return options.many;
    }

    if (count === 0) {
      return `${param(options.name, count)} ${options.many}`;
    }

    if (options.showCount === "yes") {
      return `1 ${singular}`;
    }

    return singular;
  };
}

export function useI18n() {
  return useContext(I18nContext);
}
